import { cva, type VariantProps } from 'class-variance-authority'
import { X } from 'lucide-react'
import * as React from 'react'

import { cn } from '@eigtech/ui/lib/utils'
import { useCallback } from 'react'

const alertVariants = cva(
  'group-[.toaster]:w-(--width) relative flex w-full items-stretch gap-2 overflow-hidden',
  {
    variants: {
      variant: {
        secondary: '',
        primary: '',
        destructive: '',
        success: '',
        info: '',
        mono: '',
        warning: '',
      },
      icon: {
        primary: '',
        destructive: '',
        success: '',
        info: '',
        warning: '',
      },
      appearance: {
        solid: '',
        outline: '',
        light: '',
        stroke: 'text-foreground',
      },
      size: {
        lg: '*:data-slot=alert-icon:mt-0.5 **:data-[slot=alert-close]:mt-1 gap-3 rounded-lg p-4 text-base [&>[data-slot=alert-icon]>svg]:size-6',
        md: '*:data-slot=alert-icon:mt-0 **:data-[slot=alert-close]:mt-0.5 gap-2.5 rounded-lg p-3.5 text-sm [&>[data-slot=alert-icon]>svg]:size-5',
        sm: '*:data-alert-icon:mt-0.5 **:data-[slot=alert-close]:mt-px gap-2 rounded-md px-3 py-2.5 text-xs [&>[data-slot=alert-icon]>svg]:size-4 [&_[data-slot=alert-close]_svg]:size-3.5',
      },
    },
    compoundVariants: [
      /* Solid */
      {
        variant: 'secondary',
        appearance: 'solid',
        className: 'bg-muted text-foreground',
      },
      {
        variant: 'primary',
        appearance: 'solid',
        className: 'bg-primary text-primary-foreground',
      },
      {
        variant: 'destructive',
        appearance: 'solid',
        className: 'bg-destructive text-destructive-foreground',
      },
      {
        variant: 'success',
        appearance: 'solid',
        className:
          'bg-(--color-success,var(--color-green-500)) text-(--color-success-foreground,var(--color-white))',
      },
      {
        variant: 'info',
        appearance: 'solid',
        className:
          'bg-(--color-info,var(--color-blue-600)) text-(--color-info-foreground,var(--color-white))',
      },
      {
        variant: 'warning',
        appearance: 'solid',
        className:
          'bg-(--color-warning,var(--color-yellow-500)) text-(--color-warning-foreground,var(--color-white))',
      },
      {
        variant: 'mono',
        appearance: 'solid',
        className:
          '*:data-slot-[alert=close]:text-white bg-zinc-950 text-white dark:bg-zinc-300 dark:text-black',
      },

      /* Outline */
      {
        variant: 'secondary',
        appearance: 'outline',
        className:
          'border-border bg-background text-foreground **:data-[slot=alert-close]:text-foreground border',
      },
      {
        variant: 'primary',
        appearance: 'outline',
        className:
          'border-border bg-background text-primary **:data-[slot=alert-close]:text-foreground border',
      },
      {
        variant: 'destructive',
        appearance: 'outline',
        className:
          'border-border bg-background text-destructive **:data-[slot=alert-close]:text-foreground border',
      },
      {
        variant: 'success',
        appearance: 'outline',
        className:
          'border-border bg-background **:data-[slot=alert-close]:text-foreground text-(--color-success,var(--color-green-500)) border',
      },
      {
        variant: 'info',
        appearance: 'outline',
        className:
          'border-border bg-background **:data-[slot=alert-close]:text-foreground text-(--color-info,var(--color-blue-600)) border',
      },
      {
        variant: 'warning',
        appearance: 'outline',
        className:
          'border-border bg-background **:data-[slot=alert-close]:text-foreground text-(--color-warning,var(--color-yellow-500)) border',
      },
      {
        variant: 'mono',
        appearance: 'outline',
        className:
          'border-border bg-background text-foreground **:data-[slot=alert-close]:text-foreground border',
      },

      /* Light */
      {
        variant: 'secondary',
        appearance: 'light',
        className: 'bg-muted border-border text-foreground border',
      },
      {
        variant: 'primary',
        appearance: 'light',
        className:
          'text-foreground **:data-[slot=alert-icon]:text-primary border-(--color-primary-alpha,var(--color-blue-100)) bg-(--color-primary-soft,var(--color-blue-50)) dark:border-(--color-primary-alpha,var(--color-blue-900)) dark:bg-(--color-primary-soft,var(--color-blue-950)) border',
      },
      {
        variant: 'destructive',
        appearance: 'light',
        className:
          'text-foreground **:data-[slot=alert-icon]:text-destructive border-(--color-destructive-alpha,var(--color-red-100)) bg-(--color-destructive-soft,var(--color-red-50)) dark:border-(--color-destructive-alpha,var(--color-red-900)) dark:bg-(--color-destructive-soft,var(--color-red-950)) border',
      },
      {
        variant: 'success',
        appearance: 'light',
        className:
          'text-foreground border-(--color-success-alpha,var(--color-green-200)) bg-(--color-success-soft,var(--color-green-50)) dark:border-(--color-success-alpha,var(--color-green-900)) dark:bg-(--color-success-soft,var(--color-green-950)) **:data-[slot=alert-icon]:text-(--color-success-foreground,var(--color-green-600)) border',
      },
      {
        variant: 'info',
        appearance: 'light',
        className:
          'text-foreground border-(--color-info-alpha,var(--color-blue-100)) bg-(--color-info-soft,var(--color-blue-50)) dark:border-(--color-info-alpha,var(--color-blue-900)) dark:bg-(--color-info-soft,var(--color-blue-950)) **:data-[slot=alert-icon]:text-(--color-info-foreground,var(--color-blue-600)) border',
      },
      {
        variant: 'warning',
        appearance: 'light',
        className:
          'text-foreground border-(--color-warning-alpha,var(--color-yellow-200)) bg-(--color-warning-soft,var(--color-yellow-50)) dark:border-(--color-warning-alpha,var(--color-yellow-900)) dark:bg-(--color-warning-soft,var(--color-yellow-950)) **:data-[slot=alert-icon]:text-(--color-warning-foreground,var(--color-yellow-600)) border',
      },

      /* Mono */
      {
        variant: 'mono',
        icon: 'primary',
        className: '**:data-[slot=alert-icon]:text-primary',
      },
      {
        variant: 'mono',
        icon: 'warning',
        className:
          '**:data-[slot=alert-icon]:text-(--color-warning-foreground,var(--color-yellow-600))',
      },
      {
        variant: 'mono',
        icon: 'success',
        className:
          '**:data-[slot=alert-icon]:text-(--color-success-foreground,var(--color-green-600))',
      },
      {
        variant: 'mono',
        icon: 'destructive',
        className: '**:data-[slot=alert-icon]:text-destructive',
      },
      {
        variant: 'mono',
        icon: 'info',
        className: '**:data-[slot=alert-icon]:text-(--color-info-foreground,var(--color-blue-600))',
      },
    ],
    defaultVariants: {
      variant: 'secondary',
      appearance: 'light',
      size: 'md',
    },
  }
)

interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {
  autoClose?: boolean | number
  close?: boolean
  onClose?: () => void
}

interface AlertIconProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {}

/**
 *
 * @example
 * ```tsx
  <Alert variant="destructive">
    <AlertIcon>
      <TriangleAlert />
    </AlertIcon>
    <AlertContent>
      <AlertTitle>Alert Title</AlertTitle>
      <AlertDescription>Alert Description</AlertDescription>
    </AlertContent>
    <AlertToolbar>
      <Button>Alert Action</Button>
    </AlertToolbar>
  </Alert>
 */
function Alert({
  className,
  variant,
  size,
  icon,
  appearance,
  autoClose,
  close = false,
  onClose: onCloseProp,
  children,
  ...props
}: AlertProps) {
  const [isOpen, setIsOpen] = React.useState(true)
  const [progressStarted, setProgressStarted] = React.useState(false)

  const onClose = useCallback(() => {
    setIsOpen(false)
    onCloseProp?.()
  }, [onCloseProp])

  React.useEffect(() => {
    if (!autoClose) return
    setProgressStarted(true)
    const timeout = setTimeout(onClose, autoClose === true ? DEFAULT_TIMEOUT : autoClose)

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!isOpen) return null

  return (
    <div
      className={cn(alertVariants({ variant, size, icon, appearance }), className)}
      data-slot="alert"
      role="alert"
      {...props}
    >
      {children}
      {close && (
        <button
          aria-label="Dismiss"
          className={cn('group size-4 shrink-0 cursor-pointer')}
          data-slot="alert-close"
          onClick={onClose}
        >
          <X className="size-4 opacity-60 group-hover:opacity-100" />
        </button>
      )}
      {autoClose && (
        <div className="bg-background/30 pointer-events-none absolute inset-x-0 bottom-0 h-0.5">
          <div
            className="bg-foreground/60 h-full transition-[width] ease-linear"
            style={
              {
                '--tw-duration': `${autoClose === true ? DEFAULT_TIMEOUT : autoClose}ms`,
                width: progressStarted ? '0%' : '100%',
              } as React.CSSProperties
            }
          />
        </div>
      )}
    </div>
  )
}

const DEFAULT_TIMEOUT = 10000

function AlertTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <div className={cn('grow tracking-tight', className)} data-slot="alert-title" {...props} />
}

function AlertIcon({ children, className, ...props }: AlertIconProps) {
  return (
    <div className={cn('shrink-0', className)} data-slot="alert-icon" {...props}>
      {children}
    </div>
  )
}

function AlertToolbar({ children, className, ...props }: AlertIconProps) {
  return (
    <div className={cn(className)} data-slot="alert-toolbar" {...props}>
      {children}
    </div>
  )
}

function AlertDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <div
      className={cn('text-sm [&_p]:mb-2 [&_p]:leading-relaxed', className)}
      data-slot="alert-description"
      {...props}
    />
  )
}

function AlertContent({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <div
      className={cn('**:data-[slot=alert-title]:font-semibold flex-1 space-y-2', className)}
      data-slot="alert-content"
      {...props}
    />
  )
}

export { Alert, AlertContent, AlertDescription, AlertIcon, AlertTitle, AlertToolbar }
